import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { createConsultantNetwork, getConsultantNetworks } from './api';

const parseOptionalNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
};

const countFromNetwork = (network, countKeys, collectionKeys) => {
  for (const key of countKeys) {
    const parsed = parseOptionalNumber(network?.[key]);
    if (parsed != null) return { value: parsed, isAvailable: true };
  }

  for (const key of collectionKeys) {
    if (Array.isArray(network?.[key])) return { value: network[key].length, isAvailable: true };
  }

  return { value: null, isAvailable: false };
};

const getOrganizationCount = (network) =>
  countFromNetwork(
    network,
    [
      'organizations_count',
      'organisations_count',
      'organization_count',
      'network_organizations_count',
      'network_organisations_count',
      'network_orgs_count',
      'orgs_count',
    ],
    ['organizations', 'organisations', 'network_organizations', 'network_organisations', 'orgs']
  );

const getUserCount = (network) =>
  countFromNetwork(
    network,
    ['users_count', 'network_users_count', 'network_user_count', 'members_count'],
    ['users', 'network_users', 'members']
  );

const renderCount = (countResult) => (countResult.isAvailable ? countResult.value : 'Not available');

const ConsultantNetworks = () => {
  const toast = useToast();
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [networks, setNetworks] = useState([]);
  const [form, setForm] = useState({ name: '' });

  const modal = useDisclosure();

  const loadNetworks = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = await getConsultantNetworks();
      setNetworks(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error('Error loading networks:', error);
      toast({ title: 'Could not load networks', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadNetworks();
  }, [loadNetworks]);

  const sortedNetworks = useMemo(
    () => [...networks].sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [networks]
  );

  const handleCreate = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await createConsultantNetwork({ name: form.name.trim() });
      toast({ title: 'Network created', status: 'success', duration: 3000, isClosable: true });
      setForm({ name: '' });
      modal.onClose();
      await loadNetworks();
    } catch (error) {
      console.error('Error creating network:', error);
      toast({ title: 'Could not create network', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box p={{ base: 4, md: 6 }}>
      <HStack justify="space-between" align={{ base: 'start', md: 'center' }} mb={4} flexWrap="wrap">
        <Box>
          <Heading size="lg">Networks</Heading>
          <Text color="gray.500">Create and manage consultant networks.</Text>
        </Box>
        <Button leftIcon={<FaPlus />} colorScheme="blue" variant="outline" onClick={modal.onOpen}>
          Create network
        </Button>
      </HStack>

      {isLoading ? (
        <HStack justify="center" py={12}><Spinner size="lg" /></HStack>
      ) : sortedNetworks.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody><Text>No networks yet.</Text></CardBody>
        </Card>
      ) : isMobile ? (
        <VStack align="stretch" spacing={3}>
          {sortedNetworks.map((network) => {
            const organizationsCount = getOrganizationCount(network);
            const usersCount = getUserCount(network);

            return (
            <Card key={network.id} bg={cardBg} borderWidth="1px" borderColor={borderColor}>
              <CardBody>
                <VStack align="stretch" spacing={2}>
                  <Text fontWeight="semibold">{network.name}</Text>
                  <Text fontSize="sm" color="gray.500">Schools: {renderCount(organizationsCount)}</Text>
                  <Text fontSize="sm" color="gray.500">Users: {renderCount(usersCount)}</Text>
                  <Button as={RouterLink} to={`/app/consultant/networks/${network.id}`} size="sm" variant="outline">
                    Open network
                  </Button>
                </VStack>
              </CardBody>
            </Card>
            );
          })}
        </VStack>
      ) : (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Organisations</Th>
                    <Th>Users</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedNetworks.map((network) => {
                    const organizationsCount = getOrganizationCount(network);
                    const usersCount = getUserCount(network);

                    return (
                    <Tr key={network.id}>
                      <Td fontWeight="semibold">{network.name}</Td>
                      <Td color={organizationsCount.isAvailable ? 'gray.600' : 'gray.500'}>{renderCount(organizationsCount)}</Td>
                      <Td color={usersCount.isAvailable ? 'gray.600' : 'gray.500'}>{renderCount(usersCount)}</Td>
                      <Td>
                        <Button as={RouterLink} to={`/app/consultant/networks/${network.id}`} size="sm" variant="outline">
                          Open network
                        </Button>
                      </Td>
                    </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      <Modal isOpen={modal.isOpen} onClose={modal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create network</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleCreate}>
            <ModalBody>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input value={form.name} onChange={(event) => setForm({ name: event.target.value })} />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={modal.onClose}>Cancel</Button>
              <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>Create</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ConsultantNetworks;
